import { lazy, Suspense, useMemo, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { CityLink } from "@/components/CityLink";
import { Layout } from "@/components/Layout";
import { useGoldRates } from "@/lib/nonblocking-rates";
import {
  computeCityPrices,
  currencyFor,
  formatMoney,
  getCities,
  GRAMS_PER_TOLA,
  type Country,
} from "@/lib/gold-data";
import { PremiumGoldCards } from "@/components/PremiumPriceCards";
import { HubNextSteps } from "@/components/HubNextSteps";
import { WhatsAppShareInline } from "@/components/WhatsAppShareFab";
import { PriceAlertBell } from "@/components/PriceAlertBell";
import { OverseasMatrix } from "@/components/OverseasExpat";
import { ZakatCalculator } from "@/components/ZakatCalculator";
import { Coins, LineChart as LineChartIcon, Landmark, Recycle } from "lucide-react";
import { RateMethodology } from "@/components/trust/RateMethodology";

const GoldChart = lazy(() =>
  import("@/components/GoldChart").then((m) => ({ default: m.GoldChart })),
);

/**
 * GoldCountryHubPage — shared, clean hub layout for /pakistan and /india.
 *
 * Ordering (per product spec):
 *   1. Historical chart (top-of-fold trust signal)
 *   2. Live "today's rate" hero number
 *   3. Native-script (Urdu / Hindi) CTA block
 *   4. Karat price cards (24K / 22K / 21K / 18K)
 *   5. Price alert + WhatsApp share bar
 *   6. Editorial (E-E-A-T) + calculators + diaspora + FAQ + city grid
 *
 * Zero-touch: reuses the shared live-rate engine — pricing math is unchanged.
 */

type CountryCopy = {
  regionLabel: string;
  currency: "PKR" | "INR";
  timeZone: string;
  dateLocale: string;
  tzLabel: string;
  primaryUnit: "tola" | "10g";
  liveTitle: string;
  liveHeadline: string;
  h1: string;
  updatedSuffix: string;
  ctaVariant: "gold-pk" | "gold-in";
  bodyAuthority: string;
  bodyMechanics: string;
  cityRoutePrefix: "pakistan" | "india";
  eeatTitle: string;
  faqTitle: string;
  cityGridTitle: string;
  cityGridBlurb: string;
  faqs: { q: string; a: string }[];
};

const COPY: Record<Country, CountryCopy> = {
  pakistan: {
    regionLabel: "Pakistan",
    currency: "PKR",
    timeZone: "Asia/Karachi",
    dateLocale: "en-PK",
    tzLabel: "PKT",
    primaryUnit: "tola",
    liveTitle: "Live · 24K gold · Pakistan",
    liveHeadline: "Per tola (11.66 g)",
    h1: "Today's Gold Rate in Pakistan — Live Sona Ka Bhao",
    updatedSuffix: "recalculated every 10 minutes from live XAU/USD spot & interbank USD/PKR.",
    ctaVariant: "gold-pk",
    bodyAuthority:
      "The daily gold rate in Pakistan is announced by the All-Pakistan Sarafa Gems and Jewellers Association (APSGJA) from Karachi's wholesale bullion market. Their opening quote is derived from the international spot gold benchmark (XAU/USD, priced per troy ounce on the London and New York markets), converted to Pakistani rupees at the interbank USD/PKR exchange rate and then adjusted for the local sarafa premium.",
    bodyMechanics:
      "When the dollar strengthens or global spot moves, Pakistani gold moves with it — usually within minutes. Our engine mirrors the same formula sarafa bazaars use, so the per-tola, per-10-gram and per-gram numbers on this page stay aligned with what jewellers quote at the counter. 24K is investment-grade pure gold; 22K is the standard for heavy jewellery; 21K is a popular Gulf import karat; 18K is used for stone-set and lightweight pieces.",
    cityRoutePrefix: "pakistan",
    eeatTitle: "How the gold rate in Pakistan is set",
    faqTitle: "Gold rate in Pakistan — quick answers",
    cityGridTitle: "City-wise gold rates across Pakistan",
    cityGridBlurb:
      "Every major sarafa bazaar has its own live page — per-tola, per-gram and per-10-gram breakdown for 24K, 22K and 21K.",
    faqs: [
      {
        q: "What is the difference between 22K and 24K gold in Pakistan?",
        a: "24K gold is 99.9% pure and is used for investment bars, coins and the benchmark rate quoted on this page. 22K gold is 91.6% pure — the remaining 8.4% is a copper/silver alloy that gives jewellery the strength needed for daily wear. 22K sells at roughly 91.6% of the 24K per-gram price, before making charges.",
      },
      {
        q: "Why does the gold rate in Pakistan change during the day?",
        a: "The Pakistani rate tracks two live inputs — the international XAU/USD spot price and the interbank USD/PKR rate. When either moves, the sarafa bazaars re-quote. We refresh the on-page number every 10 minutes to keep it aligned with those two feeds.",
      },
      {
        q: "How is Zakat on gold calculated in Pakistan?",
        a: "If you own 7.5 tola (87.48 g) of gold or more, Zakat is 2.5% of its current market value, payable once a lunar year. Use the Live Nisab Zakat tab above for an instant, rate-linked figure.",
      },
      {
        q: "Should I buy gold in Pakistan or bring it from Dubai / Saudi Arabia?",
        a: "Gulf gold (especially Dubai 22K and Saudi 21K) is often 3–6% cheaper per gram because of lower duty and mature bullion markets. Passengers can bring a limited quantity of personal gold to Pakistan duty-free — always confirm the current customs allowance before travelling.",
      },
      {
        q: "What are typical making charges on Pakistani gold jewellery?",
        a: "Making charges usually range from Rs 800 to Rs 3,500 per gram depending on design complexity, brand and city. Use the Purity & Making Charges tab above to estimate a bill before you visit the sarafa.",
      },
      {
        q: "How accurate is the live gold rate on this page?",
        a: "The number is derived from the same inputs the sarafa bazaars use — the live XAU/USD spot price and the interbank USD/PKR rate — plus a small local premium. It refreshes every 10 minutes. Expect a small gap versus a specific jeweller's counter quote, driven by that shop's inventory position and immediate wastage/making margins.",
      },
    ],
  },
  india: {
    regionLabel: "India",
    currency: "INR",
    timeZone: "Asia/Kolkata",
    dateLocale: "en-IN",
    tzLabel: "IST",
    primaryUnit: "10g",
    liveTitle: "Live · 24K gold · India",
    liveHeadline: "Per 10 gram",
    h1: "Today's Gold Rate in India — Aaj Ka Sone Ka Bhav",
    updatedSuffix: "recalculated every 10 minutes from live XAU/USD spot & interbank USD/INR.",
    ctaVariant: "gold-in",
    bodyAuthority:
      "The daily gold rate in India is set by the India Bullion and Jewellers Association (IBJA) from Mumbai's Zaveri Bazaar, based on the international spot benchmark (XAU/USD) converted at the interbank USD/INR rate and adjusted for 15% customs duty and 3% GST. Regional sarafa bazaars — Delhi, Chennai, Kolkata, Bangalore, Ahmedabad — then add a small local premium for handling and refining.",
    bodyMechanics:
      "When the rupee weakens or global spot moves, Indian gold moves within minutes. Our engine mirrors the IBJA formula, so the per-10-gram, per-tola and per-gram numbers stay aligned with what BIS-hallmarked jewellers quote at the counter. 24K is investment-grade pure gold; 22K is the mandatory hallmark karat for wedding jewellery; 21K is common in Kerala and Gulf-linked designs; 18K is used for diamond-set and lightweight pieces.",
    cityRoutePrefix: "india",
    eeatTitle: "How the gold rate in India is set",
    faqTitle: "Gold rate in India — quick answers",
    cityGridTitle: "City-wise gold rates across India",
    cityGridBlurb:
      "Every major sarafa bazaar has its own live page — per-10-gram, per-gram and per-tola breakdown for 24K, 22K and 21K.",
    faqs: [
      {
        q: "What is the difference between 22K and 24K gold in India?",
        a: "24K gold is 99.9% pure and used for investment bars, coins and the benchmark rate quoted on this page. 22K gold is 91.6% pure and is the BIS-hallmarked standard for jewellery — the remaining 8.4% is an alloy that gives it the strength needed for daily wear. 22K sells at roughly 91.6% of the 24K per-gram price, before making charges and GST.",
      },
      {
        q: "Why does the gold rate in India change during the day?",
        a: "The Indian rate tracks two live inputs — the international XAU/USD spot price and the interbank USD/INR rate. When either moves, IBJA and city sarafa bazaars re-quote. We refresh the on-page number every 10 minutes to keep it aligned with those two feeds.",
      },
      {
        q: "How much GST is charged on gold in India?",
        a: "Retail gold jewellery in India carries 3% GST on the metal value plus 5% GST on making charges. Investment gold (24K coins and bars) also carries 3% GST. The per-gram number on this page is the pre-GST market rate; add GST when comparing to a jeweller's final bill.",
      },
      {
        q: "What is BIS hallmarking and why does it matter?",
        a: "BIS hallmarking is mandatory for all gold jewellery sold in India. A genuine hallmark carries the BIS logo, the karat purity (e.g. 916 for 22K, 750 for 18K), a 6-digit HUID and the jeweller's mark. Always verify these four marks before paying — they guarantee the karat purity of the piece.",
      },
      {
        q: "What are typical making charges on Indian gold jewellery?",
        a: "Making charges usually range from 8% to 25% of the metal value, depending on design complexity, brand and city. Machine-made light chains sit at the low end; hand-crafted bridal and antique-finish sets sit at the high end. Use the Purity & Making Charges tab above to estimate a bill before you visit the sarafa.",
      },
      {
        q: "How accurate is the live gold rate on this page?",
        a: "The number is derived from the same inputs IBJA and city sarafa bazaars use — the live XAU/USD spot price and the interbank USD/INR rate — plus customs duty and a small local premium. It refreshes every 10 minutes. Expect a small gap versus a specific jeweller's counter quote, driven by that shop's inventory position and immediate wastage/making margins.",
      },
    ],
  },
};

function fxFor(country: Country, data: { pkrPerUsd: number; inrPerUsd: number }) {
  return country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
}

export function GoldCountryHubPage({ country }: { country: Country }) {
  return (
    <Layout>
      <ChartSection country={country} />
      <HeroSection country={country} />
      <CalculatorTabsSection country={country} />
      <CalculatorPromoSection country={country} />
      <EEATSection country={country} />
      <RateMethodology country={country} />
      <DiasporaSection country={country} />
      <FaqAccordionSection country={country} />
      <CityHubGrid country={country} />
      {country === "india" ? <HindiPagesSection /> : null}
    </Layout>
  );
}

/** Back-compat named export so existing imports keep working. */
export function PakistanHubPage() {
  return <GoldCountryHubPage country="pakistan" />;
}

function CalculatorPromoSection({ country }: { country: Country }) {
  const href =
    country === "pakistan"
      ? "/today-gold-rate-calculator-pakistan"
      : "/today-gold-rate-calculator-india";
  const heading =
    country === "pakistan"
      ? "Today Gold Rate Calculator Pakistan — 24K, 22K, 21K, 18K Per Tola"
      : "Today Gold Rate Calculator India — 24 Carat & 22Kt Per 10 Gram";
  const sub =
    country === "pakistan"
      ? "Live sarafa rate ke hisaab se apni exact quantity (tola, gram, masha, ratti) ka price seconds mein calculate karein — sabhi karat aur cities included."
      : "Live IBJA-style rate ke hisaab se 10 gram, 8 gram, per gram ya tola ka price instantly calculate karein — Delhi, Mumbai, Chennai, Kolkata, Bangalore sabhi cities."
  ;
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <a
        href={href}
        className="block rounded-2xl border border-primary/40 bg-primary/5 p-6 transition hover:bg-primary/10"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold">{heading}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
          </div>
          <span className="shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
            Open Calculator →
          </span>
        </div>
      </a>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 1 — Historical chart (top of the page)                            */
/* -------------------------------------------------------------------------- */

function ChartSection({ country }: { country: Country }) {
  return (
    <Suspense
      fallback={
        // Skeleton mirrors GoldChart's exact box model (heading + copy +
        // 288px chart area) so there is zero layout shift on hydration.
        <div className="pt-8 min-h-[480px]" aria-busy="true">
          <section className="mx-auto max-w-6xl px-4 pb-12">
            <h2 className="text-xl md:text-2xl font-bold mb-2">Last 6 months — 24K gold trend</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Daily 24K &amp; 22K rate. Rolling 6-month window — a new point is added every day and the oldest drops off.
            </p>
            <div className="rounded-xl border border-border bg-card p-4 min-h-[320px]">
              <div className="h-72 w-full rounded-lg bg-muted/30" />
            </div>
          </section>
        </div>
      }
    >
      <div className="pt-8 min-h-[480px]" style={{ containIntrinsicSize: "1px 480px" }}>
        <GoldChart country={country} />
      </div>
    </Suspense>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 2 — Hero: live number → native CTAs → karat cards → action bar    */
/* -------------------------------------------------------------------------- */

function HeroSection({ country }: { country: Country }) {
  const copy = COPY[country];
  const { data } = useGoldRates();
  const currency = currencyFor(country);
  const prices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: fxFor(country, data),
    country,
    cityPremium: 1.0,
  });
  const updated = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(copy.dateLocale, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: copy.timeZone,
      }).format(new Date(data.fetchedAt));
    } catch {
      return new Date(data.fetchedAt).toUTCString();
    }
  }, [data.fetchedAt, copy.dateLocale, copy.timeZone]);

  const primaryValue =
    copy.primaryUnit === "tola" ? prices.perTola24 : prices.per10g24;
  const secondaryUnit = copy.primaryUnit === "tola" ? "Per 10 g" : "Per tola (11.66 g)";
  const secondaryValue =
    copy.primaryUnit === "tola" ? prices.per10g24 : prices.perTola24;

  return (
    <>
      {/* H1 + timestamp */}
      <section className="mx-auto max-w-6xl px-4 pt-2 pb-2">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{copy.h1}</h1>
        <p className="mt-2 text-sm text-muted-foreground" data-nosnippet>
          Last updated{" "}
          <span data-nosnippet><time dateTime={new Date(data.fetchedAt).toISOString()} className="font-semibold text-foreground">
            {updated} {copy.tzLabel}
          </time></span>
          {" "}· {copy.updatedSuffix}
        </p>
      </section>

      {/* Big live number */}
      <section className="mx-auto max-w-6xl px-4 pb-4">
        <div className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-gradient-to-br from-card via-card to-amber-500/5 p-6 md:p-8 shadow-lg">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            {copy.liveTitle}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">{copy.liveHeadline}</div>
          <div className="mt-1 text-5xl md:text-6xl font-black tracking-tight text-amber-600 dark:text-amber-300 leading-none tabular-nums">
            {formatMoney(primaryValue, currency)}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-foreground/85">
            <span>
              <span className="text-muted-foreground">{secondaryUnit}:</span>{" "}
              <span className="font-bold tabular-nums">{formatMoney(secondaryValue, currency)}</span>
            </span>
            <span>
              <span className="text-muted-foreground">Per gram:</span>{" "}
              <span className="font-bold tabular-nums">{formatMoney(prices.perGram24, currency)}</span>
            </span>
          </div>
        </div>
      </section>

      {/* Action bar: price alert + share — directly under the live number, above native CTAs */}
      <section className="mx-auto max-w-6xl px-4 pb-4">
        <div className="flex flex-wrap items-center gap-2 min-h-[48px]">
          <PriceAlertBell country={country} metal="gold" currency={copy.currency} />
          <WhatsAppShareInline />
        </div>
      </section>

      {/* Native-script CTAs */}
      <HubNextSteps variant={copy.ctaVariant} />

      {/* Karat cards */}
      <section className="mx-auto max-w-6xl px-4">
        <PremiumGoldCards prices={prices} currency={currency} regionLabel={copy.regionLabel} />
      </section>

      {/* Global market cards: XAU/USD per ounce + USD→local FX */}
      <GlobalMarketCards
        country={country}
        spotUsdPerOz={data.spotUsdPerOz}
        fxRate={fxFor(country, data)}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Global market cards — international spot + USD FX driving the local rate  */
/* -------------------------------------------------------------------------- */

function GlobalMarketCards({
  country,
  spotUsdPerOz,
  fxRate,
}: {
  country: Country;
  spotUsdPerOz: number;
  fxRate: number;
}) {
  const fxCode = country === "pakistan" ? "PKR" : "INR";
  const fxName = country === "pakistan" ? "Pakistani Rupee" : "Indian Rupee";
  const usd = (n: number, digits = 2) =>
    `$${n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
  const local = (n: number, digits = 2) =>
    n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

  const ouncePerGram = spotUsdPerOz / 31.1035;
  const ounceInLocal = spotUsdPerOz * fxRate;

  return (
    <section aria-label="International gold spot and USD exchange rate" className="mx-auto max-w-6xl px-4 pt-8">
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Card 1 — international gold in USD per ounce */}
        <article className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-card/70 p-5 shadow-sm ring-1 ring-amber-400/20">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-400/15 via-yellow-300/5 to-transparent"
          />
          <div className="relative flex items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Gold Rate in USD — 1 Ounce
            </h2>
            <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-300">
              XAU/USD
            </span>
          </div>
          <div className="relative mt-3 text-3xl md:text-4xl font-black tracking-tight tabular-nums text-amber-600 dark:text-amber-300">
            {usd(spotUsdPerOz)}
          </div>
          <p className="relative mt-1 text-xs text-muted-foreground">
            Per troy ounce (31.1035 g) · 24K international spot
          </p>
          <div className="relative mt-3 space-y-1.5 text-sm">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-background/60 px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Per gram (USD)
              </span>
              <span className="font-bold tabular-nums">{usd(ouncePerGram)}</span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-background/60 px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                1 Ounce in {fxCode}
              </span>
              <span className="font-bold tabular-nums">
                {fxCode} {local(ounceInLocal, 0)}
              </span>
            </div>
          </div>
        </article>

        {/* Card 2 — USD to local currency */}
        <article className="relative overflow-hidden rounded-2xl border border-emerald-400/40 bg-card/70 p-5 shadow-sm ring-1 ring-emerald-400/20">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-400/15 via-teal-300/5 to-transparent"
          />
          <div className="relative flex items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
              USD to {fxCode} Today
            </h2>
            <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-300">
              USD/{fxCode}
            </span>
          </div>
          <div className="relative mt-3 text-3xl md:text-4xl font-black tracking-tight tabular-nums text-emerald-600 dark:text-emerald-300">
            {local(fxRate, 2)}
          </div>
          <p className="relative mt-1 text-xs text-muted-foreground">
            1 US Dollar = {local(fxRate, 2)} {fxName} ({fxCode})
          </p>
          <div className="relative mt-3 space-y-1.5 text-sm">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-background/60 px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                100 USD
              </span>
              <span className="font-bold tabular-nums">
                {fxCode} {local(fxRate * 100, 0)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-background/60 px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                1000 USD
              </span>
              <span className="font-bold tabular-nums">
                {fxCode} {local(fxRate * 1000, 0)}
              </span>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 3 — Tabbed calculators                                            */
/* -------------------------------------------------------------------------- */

type TabKey = "purity" | "zakat" | "resale";

function CalculatorTabsSection({ country }: { country: Country }) {
  const [tab, setTab] = useState<TabKey>("purity");
  const tabs: { key: TabKey; label: string; icon: ReactNode }[] = [
    { key: "purity", label: "Purity & Making Charges", icon: <Coins className="h-4 w-4" /> },
    { key: "zakat", label: "Live Nisab Zakat", icon: <Landmark className="h-4 w-4" /> },
    { key: "resale", label: "Purana Sona Resale", icon: <Recycle className="h-4 w-4" /> },
  ];
  return (
    <section id="gold-calc-heading" className="mx-auto max-w-6xl px-4 py-10">
      <div className="mb-4 flex items-center gap-2">
        <span className="inline-flex items-center justify-center rounded-xl bg-amber-500/10 p-2 text-amber-500 ring-1 ring-amber-400/30">
          <LineChartIcon className="h-5 w-5" />
        </span>
        <h2 className="text-xl md:text-2xl font-bold">Gold calculators — purity, zakat &amp; resale</h2>
      </div>
      <div className="rounded-2xl border border-border bg-card p-2 md:p-3 shadow-sm">
        <div role="tablist" aria-label="Gold calculators" className="flex flex-wrap gap-1 border-b border-border/60 pb-2">
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : "text-muted-foreground hover:bg-muted/50"
                }`}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
        <div className="p-3 md:p-5">
          {tab === "purity" && <PurityMakingCalc country={country} />}
          {tab === "zakat" && <ZakatCalculator />}
          {tab === "resale" && <ResaleCalc country={country} />}
        </div>
      </div>
    </section>
  );
}

function usePricesFor(country: Country) {
  const { data } = useGoldRates();
  return computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: fxFor(country, data),
    country,
    cityPremium: 1.0,
  });
}

function PurityMakingCalc({ country }: { country: Country }) {
  const prices = usePricesFor(country);
  const currency = currencyFor(country);
  const currencySymbol = currency === "PKR" ? "Rs" : "₹";
  const [karat, setKarat] = useState<24 | 22 | 21 | 18>(22);
  const [grams, setGrams] = useState<number>(11.66);
  const [makingPerGram, setMakingPerGram] = useState<number>(country === "pakistan" ? 1200 : 400);
  const perGram =
    karat === 24 ? prices.perGram24
    : karat === 22 ? prices.perGram22
    : karat === 21 ? prices.perGram21
    : prices.perGram24 * 0.75;
  const metal = perGram * grams;
  const making = makingPerGram * grams;
  const total = metal + making;
  return (
    <div className="grid gap-4 md:grid-cols-[1fr_auto]">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs font-medium">
          Karat
          <select
            value={karat}
            onChange={(e) => setKarat(Number(e.target.value) as 24 | 22 | 21 | 18)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value={24}>24K (99.9%)</option>
            <option value={22}>22K (91.6%)</option>
            <option value={21}>21K (87.5%)</option>
            <option value={18}>18K (75.0%)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          Weight (grams)
          <input
            type="number"
            min={0}
            step="0.01"
            value={grams}
            onChange={(e) => setGrams(Math.max(0, parseFloat(e.target.value) || 0))}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
          <span className="text-[10px] text-muted-foreground">1 tola = {GRAMS_PER_TOLA} g</span>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          Making charges ({currencySymbol} / gram)
          <input
            type="number"
            min={0}
            step="1"
            value={makingPerGram}
            onChange={(e) => setMakingPerGram(Math.max(0, parseFloat(e.target.value) || 0))}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
      </div>
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 min-w-[200px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Estimated bill</div>
        <div className="mt-1 text-xs text-muted-foreground">Metal value</div>
        <div className="text-sm font-bold tabular-nums">{formatMoney(metal, currency)}</div>
        <div className="mt-1 text-xs text-muted-foreground">Making charges</div>
        <div className="text-sm font-bold tabular-nums">{formatMoney(making, currency)}</div>
        <div className="mt-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">Total</div>
        <div className="text-lg font-black tabular-nums text-amber-700 dark:text-amber-300">
          {formatMoney(total, currency)}
        </div>
      </div>
    </div>
  );
}

function ResaleCalc({ country }: { country: Country }) {
  const prices = usePricesFor(country);
  const currency = currencyFor(country);
  const [karat, setKarat] = useState<24 | 22 | 21 | 18>(22);
  const [grams, setGrams] = useState<number>(11.66);
  const [deduction, setDeduction] = useState<number>(8);
  const perGram =
    karat === 24 ? prices.perGram24
    : karat === 22 ? prices.perGram22
    : karat === 21 ? prices.perGram21
    : prices.perGram24 * 0.75;
  const market = perGram * grams;
  const resale = market * (1 - deduction / 100);
  return (
    <div className="grid gap-4 md:grid-cols-[1fr_auto]">
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs font-medium">
          Karat
          <select
            value={karat}
            onChange={(e) => setKarat(Number(e.target.value) as 24 | 22 | 21 | 18)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          >
            <option value={24}>24K</option>
            <option value={22}>22K</option>
            <option value={21}>21K</option>
            <option value={18}>18K</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          Weight (grams)
          <input
            type="number"
            min={0}
            step="0.01"
            value={grams}
            onChange={(e) => setGrams(Math.max(0, parseFloat(e.target.value) || 0))}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium">
          Sarafa deduction ({deduction}%)
          <input
            type="range"
            min={3}
            max={15}
            step={0.5}
            value={deduction}
            onChange={(e) => setDeduction(parseFloat(e.target.value))}
          />
          <span className="text-[10px] text-muted-foreground">Typical sarafa buy-back: 5–10%.</span>
        </label>
      </div>
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 min-w-[200px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Estimated resale</div>
        <div className="mt-1 text-xs text-muted-foreground">Market value</div>
        <div className="text-sm font-bold tabular-nums">{formatMoney(market, currency)}</div>
        <div className="mt-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">You&apos;ll likely receive</div>
        <div className="text-lg font-black tabular-nums text-amber-700 dark:text-amber-300">
          {formatMoney(resale, currency)}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 4 — E-E-A-T editorial content                                     */
/* -------------------------------------------------------------------------- */

function EEATSection({ country }: { country: Country }) {
  const copy = COPY[country];
  return (
    <section className="mx-auto max-w-4xl px-4 py-10">
      <h2 className="text-xl md:text-2xl font-bold mb-3">{copy.eeatTitle}</h2>
      <div className="prose prose-sm md:prose-base max-w-none text-foreground/85 leading-relaxed">
        <p>{copy.bodyAuthority}</p>
        <p>{copy.bodyMechanics}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 5 — Overseas diaspora matrix                                       */
/* -------------------------------------------------------------------------- */

function DiasporaSection({ country }: { country: Country }) {
  return (
    <div className="py-4">
      <OverseasMatrix country={country} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 6 — Clean FAQ accordions                                          */
/* -------------------------------------------------------------------------- */

function FaqAccordionSection({ country }: { country: Country }) {
  const copy = COPY[country];
  return (
    <section className="mx-auto max-w-4xl px-4 py-10">
      <h2 className="text-xl md:text-2xl font-bold mb-4">{copy.faqTitle}</h2>
      <div className="divide-y divide-border rounded-2xl border border-border bg-card">
        {copy.faqs.map((f, i) => (
          <details key={i} className="group open:bg-muted/20">
            <summary className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm md:text-base font-semibold hover:bg-muted/30">
              <span>{f.q}</span>
              <span className="text-amber-500 transition-transform group-open:rotate-45">＋</span>
            </summary>
            <div className="px-4 pb-4 text-sm text-foreground/85 leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: copy.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 7 — City hub grid                                                  */
/* -------------------------------------------------------------------------- */

function CityHubGrid({ country }: { country: Country }) {
  const copy = COPY[country];
  const cities = getCities(country);
  return (
    <section className="mx-auto max-w-6xl px-4 py-10" aria-label={`${copy.regionLabel} city gold pages`}>
      <h2 className="text-xl md:text-2xl font-bold mb-2">{copy.cityGridTitle}</h2>
      <p className="mb-4 text-sm text-muted-foreground">{copy.cityGridBlurb}</p>
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
        {cities.map((c) => (
          <CityLink
            key={c.slug}
            country={copy.cityRoutePrefix}
            city={c.slug}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:border-amber-400/60 hover:bg-amber-500/5 transition-colors"
          >
            Gold rate {c.name} →
          </CityLink>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section 8 — Hindi pages (India only)                                       */
/* -------------------------------------------------------------------------- */

const HINDI_PAGES: { href: string; title: string; blurb: string }[] = [
  {
    href: "/in/hi/aaj-ka-sona-chandi-bhav",
    title: "आज का सोना चांदी भाव",
    blurb: "भारत में आज का 22K/24K सोना और चांदी का लाइव भाव — प्रति ग्राम, 10 ग्राम और तोला।",
  },
  {
    href: "/in/hi/gold-rate-22k-24k",
    title: "22 कैरेट और 24 कैरेट सोने का रेट",
    blurb: "22K आभूषण vs 24K निवेश शुद्ध सोना — मेकिंग चार्ज, GST और कैरेट तुलना।",
  },
  {
    href: "/in/hi/gold-silver-city-rates",
    title: "शहर-अनुसार सोना चांदी रेट",
    blurb: "अहमदाबाद, कानपुर, हरियाणा, राजस्थान, दिल्ली, मुंबई — सभी प्रमुख शहरों का लाइव बुलियन भाव।",
  },
  {
    href: "/in/hi/gold-weight-per-kilo",
    title: "सोना कितने रुपए किलो है",
    blurb: "1 किलोग्राम शुद्ध 24K/22K सोने का भाव — थोक व निवेश खरीदारों के लिए बुलियन कैलकुलेटर।",
  },
];

function HindiPagesSection() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-10" aria-label="Hindi gold rate pages">
      <h2 className="text-xl md:text-2xl font-bold mb-2">हिंदी में सोने चांदी का रेट पढ़ें</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Prefer Hindi? भारत के सोने-चांदी के भाव, कैरेट तुलना, शहर-अनुसार रेट और प्रति किलो बुलियन कैलकुलेटर — सब हिंदी में।
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {HINDI_PAGES.map((p) => (
          <a
            key={p.href}
            href={p.href}
            className="block rounded-xl border border-amber-500/30 bg-card p-4 hover:border-amber-400/70 hover:bg-amber-500/5 transition-colors"
          >
            <div className="text-base font-bold text-foreground">{p.title} →</div>
            <p className="mt-1 text-xs md:text-sm text-muted-foreground">{p.blurb}</p>
          </a>
        ))}
      </div>
    </section>
  );
}